import React, { useEffect, useState } from "react";
import Layout from "./Layout";
import { getProducts } from "../services/api";
import { useParams, Link } from "react-router-dom";
import { FaSearch, FaTimes } from "react-icons/fa";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sort, setSort] = useState("default");

  // Search
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { category } = useParams();
  const LIMIT = 8;
  const initialLoad = React.useRef(false);

  // Load initial products
  useEffect(() => {
    // if (initialLoad.current) return; // ⛔ Skip second execution in Strict Mode
    // initialLoad.current = true;

    const timer = setTimeout(() => {
      setProducts([]);
      setPage(1);
      setTotalPages(1);

      fetchProducts(1, true);
    }, 400);

    return () => clearTimeout(timer);
  }, [category, sort, search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchProducts = async (pageNum, reset = false) => {
    const skip = (pageNum - 1) * LIMIT;

    // Don't fetch unnecessary pages
    if (!reset && pageNum > totalPages) return;

    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);

    const data = await getProducts(category, skip, LIMIT, search, sort);

    setTotalPages(Math.ceil(data.total / LIMIT));

    // append products
    setProducts((prev) => [...prev, ...data.items]);

    setPage(pageNum);
    setLoading(false);
    setLoadingMore(false);
  };

  // Infinite scroll listener
  useEffect(() => {
    const onScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 300
      ) {
        if (!loadingMore && page < totalPages) {
          fetchProducts(page + 1);
        }
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [loadingMore, page, totalPages]);

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-20 text-gray-300 text-xl">
          Loading products...
        </div>
      </Layout>
    );
  }

  const cloudinarySquare = (url) => {
    if (!url.includes("/upload/")) return url;

    return url.replace(
      "/upload/",
      "/upload/c_fit,w_700,h_700,b_white,f_auto,q_auto/",
    );
  };

  return (
    <Layout>
      <section className="text-center px-6 md:px-16 pt-8 max-w-6xl mx-auto">
        <h2 className="text-5xl font-serif text-light_primary mb-4">
          {category
            ? category
                .replace(/-/g, " ")
                .replace(/\b\w/g, (char) => char.toUpperCase())
            : "Our Products"}{" "}
        </h2>

        <p className="text-lg text-black mb-6">
          Explore our curated selection of gifting articles, tea & coffee sets,
          antique tables, and luxury decor.
        </p>

        {category === "antique-collections" ? (
          ""
        ) : (
          <div className="text-center px-3">
            <a
              href="/contact"
              className="
      inline-flex flex-col sm:flex-row items-center justify-center 
      text-sm sm:text-lg font-bold text-white 
      bg-gradient-to-r from-red-500 via-orange-500 to-pink-500 
      px-3 py-2 rounded-md shadow-lg 
      gap-2 sm:gap-3
      break-words text-center
    "
            >
              <span>
                🎉 We Accept Bulk Orders – Get Special Wholesale Pricing!
              </span>

              <span className="flex items-center gap-2">
                <span
                  className="
        inline-flex items-center justify-center 
        bg-white text-red-500 rounded-full 
        w-6 h-6 text-sm
      "
                >
                  📞
                </span>
                Contact Us Now
              </span>
            </a>
          </div>
        )}
      </section>

      {/* Product Grid */}
      <section className="px-6 md:px-16 py-12 mx-auto">
        {/* Search + Sort */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          {/* Search */}
          <div className="relative w-full md:max-w-md">
            {/* Search Icon */}
            <FaSearch
              className=" absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              size={16}
            />

            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search products..."
              className="w-full border border-[#e6dac7] rounded-xl px-11 py-3 pr-12 text-black bg-white outline-none shadow-sm transition-all duration-200 focus:border-light_primary focus:ring-2 focus:ring-light_primary/20 placeholder:text-gray-400"
            />

            {/* Clear Search */}
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200"
                aria-label="Clear search"
              >
                <FaTimes size={14} />
              </button>
            )}
          </div>
          {/* sort by */}
          <div className="flex justify-end mb-8">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border border-[#e6dac7] rounded-lg px-4 py-3 text-black bg-white outline-none focus:border-light_primary min-w-[220px]"
            >
              <option value="default">Sort By</option>

              <option value="price_asc">Price: Low to High</option>

              <option value="price_desc">Price: High to Low</option>

              <option value="name_asc">Name: A to Z</option>

              <option value="name_desc">Name: Z to A</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {products.map((prod) => (
            <div
              key={prod._id}
              className="group bg-white/5 backdrop-blur-md border border-[#e6dac7] rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition duration-300"
            >
              <Link to={`/product/${prod._id}`} state={{ product: prod }}>
                {/* Image */}
                <div className="aspect-square bg-white flex items-center justify-center p-4">
                  <img
                    src={cloudinarySquare(prod.image[0])}
                    className="w-full h-full object-contain"
                  />
                </div>
                {/* <div className="w-full h-60 2bg-white flex items-center justify-center overflow-hidden">
                  <img
                    src={cloudinarySquare(prod.image?.[0])}
                    alt={prod.name}
                    className="max-h-full max-w-full object-contain transition duration-500 group-hover:scale-105"
                  />
                </div> */}

                {/* Content */}
                <div className="p-4 text-center">
                  <h4 className="font-medium text-lg tracking-wide text-black">
                    {prod.name}
                  </h4>

                  <div className="flex justify-center items-center gap-3 mt-2">
                    {prod?.categoryId === "69dc8fcfdd2bdcff6f2c478b" ? (
                      ""
                    ) : (
                      <p
                        className={`text-light_primary font-semibold text-lg ${
                          prod.qty === 0 ? "line-through opacity-50" : ""
                        }`}
                      >
                        ₹{prod.price}
                      </p>
                    )}

                    {prod.qty === 0 && (
                      <span className="text-xs text-red-400 bg-red-900/30 px-2 py-1 rounded-full">
                        Out of stock
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
        {loadingMore && (
          <div className="text-center py-6 text-gray-400">Loading more...</div>
        )}
        {page >= totalPages && (
          <div className="text-center py-6 text-gray-500">No more products</div>
        )}
      </section>
    </Layout>
  );
}
